// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { AffiliateData, CreateAffiliateRequest } from '@/types/affiliate';
import { syncLegacyTablesOnReferralCodeChange } from '@/utils/affiliateUtils';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface EditAffiliateModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  initialData?: AffiliateData;
  onSave: (data: AffiliateData) => void;
}

const EditAffiliateModal: React.FC<EditAffiliateModalProps> = ({
  isOpen,
  onClose,
  mode,
  initialData,
  onSave,
}) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState<CreateAffiliateRequest & { referralCode?: string }>({
    name: '',
    email: '',
    username: '',
    password: '',
    status: 'pending',
    commissionRate: 10,
    referralCode: '',
  });

  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    username?: string;
    password?: string;
    referralCode?: string;
  }>({});

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && initialData) {
        setFormData({
          name: initialData.name,
          email: initialData.email,
          username: '', // We don't store username in the current schema
          password: '', // Don't populate password for edit mode
          status: initialData.status,
          commissionRate: initialData.commission_rate,
          referralCode: initialData.referral_code || '',
        });
      } else {
        // Reset form for create mode
        setFormData({
          name: '',
          email: '',
          username: '',
          password: '',
          status: 'pending',
          commissionRate: 10,
          referralCode: '',
        });
      }
      setErrors({});
    }
  }, [isOpen, mode, initialData]);

  const generateReferralCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = 'AFF';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const validateForm = () => {
    const newErrors: { name?: string; email?: string; username?: string; password?: string; referralCode?: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (mode === 'create') {
      if (!formData.username.trim()) {
        newErrors.username = 'Username is required';
      }

      if (!formData.password?.trim()) {
        newErrors.password = 'Password is required';
      }
    }

    // Validate referral code in edit mode
    if (mode === 'edit' && formData.referralCode && formData.referralCode.trim()) {
      const referralCode = formData.referralCode.trim().toUpperCase();
      if (!/^[A-Z0-9]{6,12}$/.test(referralCode)) {
        newErrors.referralCode = 'Referral code must be 6-12 characters, letters and numbers only';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkReferralCodeExists = async (referralCode: string, excludeUserId?: string) => {
    const query = supabase
      .from('affiliates')
      .select('id, user_id')
      .eq('referral_code', referralCode);

    if (excludeUserId) {
      query.neq('user_id', excludeUserId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error checking referral code:', error);
      return false;
    }

    return data && data.length > 0;
  };

  const ensureAffiliateRole = async (_profileId: string) => {
    // RLevents migration: role_assignments table dropped. Roles now live in
    // public.user_roles and are managed via Supabase admin / migrations.
    // This is intentionally a no-op so the affiliate-create flow doesn't 500.
    return;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      if (mode === 'create') {
        // Affiliate creation is not available in RecruitLook Events: the
        // inherited create-affiliate function was removed (it wrote to
        // affiliates/affiliate_referral_links, absent from this database).
        throw new Error('Creating affiliates is not available in this project yet.');
        const data: any = null;

        if (!data.success) {
          throw new Error(data.error || 'Failed to create affiliate');
        }

        // Ensure the affiliate role is assigned
        await ensureAffiliateRole(data.data.user_id);

        toast({
          title: "Success",
          description: "Affiliate created successfully",
        });

        // Pass the created affiliate data back with all required fields
        onSave({
          id: data.data.id,
          user_id: data.data.user_id || '',
          name: data.data.name,
          email: data.data.email,
          status: data.data.status,
          commission_rate: data.data.commissionRate,
          referral_code: data.data.referralCode,
          total_earnings: 0,
          created_at: new Date().toISOString()
        });
      } else {
        // Edit mode - update existing affiliate
        if (!initialData) {
          throw new Error('No initial data provided for edit mode');
        }

        // Handle referral code - use provided value or generate new one if empty
        let referralCode = formData.referralCode?.trim().toUpperCase() || initialData.referral_code;
        const originalReferralCode = initialData.referral_code;
        
        if (!referralCode) {
          do {
            referralCode = generateReferralCode();
          } while (await checkReferralCodeExists(referralCode, initialData.user_id));
        } else if (referralCode !== initialData.referral_code) {
          // Check if the new referral code is already taken
          const codeExists = await checkReferralCodeExists(referralCode, initialData.user_id);
          if (codeExists) {
            setErrors({ referralCode: 'This referral code is already in use' });
            setIsLoading(false);
            return;
          }
        }

        // Update profiles table
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            name: formData.name,
            email: formData.email,
          })
          .eq('id', initialData.user_id);

        if (profileError) {
          console.error('Profile update error:', profileError);
          toast({
            title: "Error",
            description: `Failed to update profile: ${profileError.message}`,
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }

        // Update affiliates table with the referral code
        const { error: affiliateError } = await supabase
          .from('affiliates')
          .update({
            status: formData.status,
            commission_rate: formData.commissionRate,
            referral_code: referralCode,
          })
          .eq('user_id', initialData.user_id);

        if (affiliateError) {
          console.error('Affiliate update error:', affiliateError);
          toast({
            title: "Error",
            description: `Failed to update affiliate: ${affiliateError.message}`,
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }

        // If referral code changed, sync legacy tables
        if (referralCode !== originalReferralCode) {
          console.log('Referral code changed, syncing legacy tables...');
          await syncLegacyTablesOnReferralCodeChange(initialData.user_id, referralCode);
        }

        // Ensure the affiliate role is assigned (in case it was missing)
        await ensureAffiliateRole(initialData.user_id);

        toast({
          title: "Success",
          description: "Affiliate updated successfully",
        });
        
        // Pass back the updated data with required fields
        onSave({
          ...initialData,
          name: formData.name,
          email: formData.email,
          status: formData.status,
          commission_rate: formData.commissionRate,
          referral_code: referralCode,
        });
      }

      onClose();
    } catch (error: any) {
      console.error('Error saving affiliate:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save affiliate",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500 text-white">Active</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500 text-white">Pending</Badge>;
      case 'suspended':
        return <Badge className="bg-orange-500 text-white">Suspended</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Create New Affiliate' : 'Edit Affiliate'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create' 
              ? 'Add a new affiliate partner to the program.'
              : 'Update affiliate partner information and settings.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Full Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter full name"
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Username - only show in create mode */}
          {mode === 'create' && (
            <div className="space-y-2">
              <Label htmlFor="username">Username *</Label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="Enter username"
                className={errors.username ? 'border-red-500' : ''}
              />
              {errors.username && (
                <p className="text-sm text-red-500">{errors.username}</p>
              )}
            </div>
          )}

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="Enter email address"
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Password - only show in create mode */}
          {mode === 'create' && (
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter password"
                className={errors.password ? 'border-red-500' : ''}
              />
              {errors.password && (
                <p className="text-sm text-red-500">{errors.password}</p>
              )}
            </div>
          )}

          {/* Status */}
          <div className="space-y-2">
            <Label>Status</Label>
            <div className="flex items-center gap-3">
              <Select 
                value={formData.status} 
                onValueChange={(value: 'pending' | 'active' | 'suspended' | 'rejected') => 
                  setFormData({ ...formData, status: value })
                }
              >
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              {getStatusBadge(formData.status)}
            </div>
          </div>

          {/* Commission Rate */}
          <div className="space-y-2">
            <Label htmlFor="commissionRate">Commission Rate (%)</Label>
            <Input
              id="commissionRate"
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={formData.commissionRate}
              onChange={(e) => setFormData({ 
                ...formData, 
                commissionRate: parseFloat(e.target.value) || 0 
              })}
              placeholder="10"
            />
          </div>

          {/* Referral Code - editable in edit mode */}
          {mode === 'edit' && (
            <div className="space-y-2">
              <Label htmlFor="referralCode">Referral Code</Label>
              <Input
                id="referralCode"
                value={formData.referralCode}
                onChange={(e) => setFormData({ ...formData, referralCode: e.target.value })}
                placeholder="Enter referral code (leave empty to auto-generate)"
                className={errors.referralCode ? 'border-red-500' : ''}
              />
              {errors.referralCode && (
                <p className="text-sm text-red-500">{errors.referralCode}</p>
              )}
              <p className="text-xs text-gray-500">
                Leave empty to auto-generate a new code, or enter a custom code (6-12 characters, letters and numbers only)
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Saving...' : (mode === 'create' ? 'Create Affiliate' : 'Save Changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditAffiliateModal;
